import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css, useTheme } from 'styled-components';

import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td, Thumbnail } from '@/components/Table';
import Text from '@/components/Text';
import DistributionScheduleTipsModal from '@/pages/DistributionSchedule/components/DistributionScheduleTipsModal';
import DistributionScheduleTipsTable from '@/pages/DistributionSchedule/components/DistributionScheduleTipsTable';
import DistributionTypeBadge from '@/pages/DistributionSchedule/components/DistributionTypeBadge';
import { DistributionVideoResource } from '@/types/resources';
import { numFormatter } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import CopyToDQDrawer from './CopyToDQDrawer';
import DistributionChannelBadge from './DistributionChannelBadge';
import DistributionRescheduleModal from './DistributionRescheduleModal';
import ReleaseInformationEdit from './ReleaseInformationEdit';

const StyledDistributionTypeBadge = styled(DistributionTypeBadge)`
  margin-top: -5px;
`;

const InnerTableTd = styled.td`
  padding: 0 !important;
`;

const ExpandButton = styled.button<{ $open: boolean }>`
  background: ${({ theme }) => theme.colors.grayscale};
  width: 14px;
  height: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  flex-shrink: 0;
  svg {
    transition: transform ease-out 0.2s;
  }
  ${({ $open }) =>
    $open &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const IdHolder = styled.div`
  display: flex;
  align-items: flex-start;
`;

const VideoLink = styled.a`
  display: inline-flex;
  align-items: center;
  max-width: 100px;
  overflow: hidden;
  div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  svg {
    margin-right: 4px;
    flex-shrink: 0;
  }
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 140px;
`;

interface Props {
  video: DistributionVideoResource;
}

const DistributionVideoTableRow: FC<Props> = ({ video }) => {
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showDQModal, setShowDQModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [showVideosTable, setShowVideosTable] = useState(false);

  const [showTipsModal, setShowTipsModal] = useState(false);

  const theme = useTheme();

  const tableViewOptions = useSelector(
    (rootState: RootState) =>
      rootState.options.tableViews['distributionVideoSummaryTable'],
  );

  const tableViewOptionsArray = Object.keys(tableViewOptions).map(
    (key) => tableViewOptions[key],
  );

  return (
    <>
      <tr>
        <Td>
          <IdHolder>
            <ExpandButton
              $open={showVideosTable}
              onClick={() => setShowVideosTable((prev) => !prev)}
            >
              <svg
                width="8"
                height="6"
                viewBox="0 0 8 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1.5L4 4.5L7 1.5"
                  stroke="#4136CF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ExpandButton>
            {video.projectId}
          </IdHolder>
        </Td>
        <Td>
          <Thumbnail src={video.thumbnail} />
        </Td>
        <Td>
          <Text $type="smallHeadline">{video.title}</Text>
        </Td>
        {tableViewOptions['Fresh ID'] && <Td>{video.freshId}</Td>}
        {tableViewOptions['Network & Channel'] && (
          <Td>
            <DistributionChannelBadge
              channel={video.channel.name}
              thumbnail={video.channel.thumbnail}
            />
          </Td>
        )}
        {tableViewOptions['Type'] && <Td>{video.isReel ? 'Reel' : 'Video'}</Td>}
        {tableViewOptions['Compilation Type'] && (
          <Td>
            <StyledDistributionTypeBadge type={video.compilationType} />
          </Td>
        )}
        {tableViewOptions['Scheduled Date'] && (
          <Td>
            {video.createdAt
              ? DateTime.fromISO(video.createdAt).toLocaleString()
              : '-'}
          </Td>
        )}
        {tableViewOptions['Release Date'] && (
          <Td>
            {video.releasedAt
              ? DateTime.fromISO(video.releasedAt).toLocaleString()
              : '-'}
          </Td>
        )}
        {tableViewOptions['Total Views'] && (
          <Td>{video.views ? numFormatter(video.views) : '-'}</Td>
        )}
        {tableViewOptions['Director'] && (
          <Td>
            {video.employees?.find((e) => e.release_role === 'director')
              ?.name ?? '-'}
          </Td>
        )}
        {tableViewOptions['Editor'] && (
          <Td>
            {video.employees?.find((e) => e.release_role === 'editor')?.name ??
              '-'}
          </Td>
        )}
        {tableViewOptions['Publisher'] && (
          <Td>
            {video.employees?.find((e) => e.release_role === 'publisher')
              ?.name ?? '-'}
          </Td>
        )}
        {tableViewOptions['Archivist'] && (
          <Td>
            {video.employees?.find((e) => e.release_role === 'archivist')
              ?.name ?? '-'}
          </Td>
        )}
        {tableViewOptions['Link'] && (
          <Td>
            {video.link ? (
              <VideoLink href={video.link} target="_blank">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="14"
                    height="14"
                    fill={theme.colors.grayscaleLight}
                  />
                  <path
                    d="M5.69434 8.30519L8.30519 5.69434"
                    stroke={theme.colors.gray}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.6523 8.95851L6.56444 10.0464C6.2182 10.3925 5.74866 10.5869 5.25908 10.5869C4.76951 10.5868 4.29999 10.3923 3.95381 10.0461C3.60763 9.69996 3.41313 9.23045 3.41309 8.74087C3.41304 8.2513 3.60747 7.78175 3.95359 7.43551L5.04144 6.34766"
                    stroke={theme.colors.gray}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.95851 7.65236L10.0464 6.5645C10.3925 6.21826 10.5869 5.74872 10.5869 5.25914C10.5868 4.76957 10.3923 4.30006 10.0461 3.95387C9.69996 3.60769 9.23045 3.41319 8.74088 3.41315C8.2513 3.41311 7.78176 3.60753 7.43551 3.95365L6.34766 5.04151"
                    stroke={theme.colors.gray}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>{video.link}</div>
              </VideoLink>
            ) : (
              '-'
            )}
          </Td>
        )}
        {tableViewOptions['Part In'] && <Td>{video.part ?? '-'}</Td>}
        {tableViewOptions['Compilation Group'] && (
          <Td>{video.compilationGroup ?? '-'}</Td>
        )}
        {tableViewOptions['Recipe Status'] && (
          <Td>{video.tipsCount ? 'Yes' : 'No'}</Td>
        )}
        {tableViewOptions['Description'] && <Td>{video.description}</Td>}
        {tableViewOptions['Notes'] && <Td>{video.notes || '-'}</Td>}
        <Td $actionsCol>
          <ActionsWrap>
            <StyledActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowRescheduleModal(true)}
              >
                <span>
                  <CalendarIcon />
                </span>
                Schedule
              </ActionsDropdownButton>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowDQModal(true)}
              >
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7.5 7.5h-.75A2.25 2.25 0 0 0 4.5 9.75v7.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-7.5a2.25 2.25 0 0 0-2.25-2.25h-.75m-6 3.75 3 3m0 0 3-3m-3 3V1.5m6 9h.75a2.25 2.25 0 0 1 2.25 2.25v7.5a2.25 2.25 0 0 1-2.25 2.25h-7.5a2.25 2.25 0 0 1-2.25-2.25v-.75"
                    />
                  </svg>
                </span>
                Copy to DQ
              </ActionsDropdownButton>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowEditModal(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
            </StyledActionsDropdown>
          </ActionsWrap>
          <AnimatePresence>
            {showRescheduleModal && (
              <DistributionRescheduleModal
                release={video}
                onClose={() => setShowRescheduleModal(false)}
              />
            )}
            {showEditModal && (
              <ReleaseInformationEdit
                releaseId={video.id}
                onClose={() => setShowEditModal(false)}
              />
            )}
            {showDQModal && (
              <CopyToDQDrawer
                release={video}
                onClose={() => setShowDQModal(false)}
              />
            )}
          </AnimatePresence>
        </Td>
      </tr>
      {!!showVideosTable && (
        <>
          <tr>
            <InnerTableTd
              colSpan={
                tableViewOptionsArray.filter((item) => item === true).length + 4
              }
            >
              <DistributionScheduleTipsTable
                tips={video.tips ?? []}
                releaseId={video.id}
                onAddTipClick={() => setShowTipsModal(true)}
              />
              <AnimatePresence>
                {showTipsModal && (
                  <DistributionScheduleTipsModal
                    onClose={() => setShowTipsModal(false)}
                    release={video}
                  />
                )}
              </AnimatePresence>
            </InnerTableTd>
          </tr>
        </>
      )}
    </>
  );
};

export default DistributionVideoTableRow;
